import { UOM, UOMResponse, ApiResponse } from "../models/uomtypes";


export const UNAUTHORIZED_EVENT = "unauthorized_access";

const getAuthToken = () => localStorage.getItem("authToken") || "";


const API_URL = process.env.REACT_APP_API_URL || "https://api-agamhive.agamvanigam.com";

interface FetchParams {
    page?: number;
    limit?: number;
    search?: string;
    filter?: Record<string, string | number | boolean>;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}


const handleUnauthorized = () => {
    console.error("Unauthorized access. Dispatching event for navigation...");
    localStorage.removeItem("authToken");
    
    const unauthorizedEvent = new CustomEvent(UNAUTHORIZED_EVENT);
    window.dispatchEvent(unauthorizedEvent);
    return { error: "Unauthorized. Please log in." };
};

export const fetchUomData = async (params: FetchParams = {}): Promise<ApiResponse<UOMResponse>> => {
    const token = getAuthToken();
    if (!token) return handleUnauthorized();

    try {
        const queryParams = new URLSearchParams({
            page: (params.page || 1).toString(),
            limit: (params.limit || 10).toString(),
            ...(params.search ? { search: params.search } : {}),
            ...(params.filter
                ? Object.fromEntries(Object.entries(params.filter).map(([key, value]) => [key, String(value)]))
                : {}),
            ...(params.sortBy ? { sortBy: params.sortBy } : {}),
            ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
        });

        const response = await fetch(`${API_URL}/uoms?${queryParams}`, {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        });

        if (response.status === 401) {
            return handleUnauthorized();
        }

        const jsonResponse = await response.json();
        if (!response.ok) {
            return { error: jsonResponse.msg || "Failed to fetch UOMs" };
        }

        return {
            data: {
                items: jsonResponse.data.data || [],
                total: jsonResponse.data.pagination.total || 0,
                totalPages: jsonResponse.data.pagination.totalPages || 1
            }
        };
    } catch (error) {
        return { error: "Network error. Please try again." };
    }
};


export const createUom = async (uom: Omit<UOM, 'status'>): Promise<ApiResponse<UOM>> => {
    const token = getAuthToken();
    if (!token) return handleUnauthorized();

    try {
        const response = await fetch(`${API_URL}/uoms`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(uom),
        });

        if (response.status === 401) {
            return handleUnauthorized();
        }

        const data = await response.json();
        return response.ok ? { data } : { error: data?.msg || "Failed to create UOM." };
    } catch (error) {
        return { error: "Network error. Please check your connection and try again." };
    }
};


export const updateUom = async (id: string, uom: UOM): Promise<ApiResponse<UOM>> => {
    const token = getAuthToken();
    if (!token) return handleUnauthorized();

    const requestBody = {
        code: uom.code,
        name: uom.name,
        status: uom.status,
        updatedAt: new Date().toISOString(),
    };

    try {
        const response = await fetch(`${API_URL}/uoms/${id}`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(requestBody),
        });

        if (response.status === 401) {
            return handleUnauthorized();
        }

        const data = await response.json();
        if (!response.ok) {
            return { error: data?.msg || "Failed to update UOM." };
        }

        return { data };
    } catch (error) {
        return { error: "Network error. Please try again." };
    }
};

export const deleteUom = async (id: string): Promise<ApiResponse<null>> => {
    const token = getAuthToken();
    if (!token) return handleUnauthorized();

    try {
        const response = await fetch(`${API_URL}/uoms/${id}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        if (response.status === 401) {
            return handleUnauthorized();
        }

        return response.ok ? { data: null } : { error: "Failed to delete UOM." };
    } catch (error) {
        return { error: "Network error. Please try again." };
    }
};